import React, { useState, useEffect, useRef } from "react";
import { petAPI, clinicAPI } from "../../services/api";
import { showErrorToast } from "../../utils/notificationUtils";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useTranslation } from "react-i18next";

const EditAppointmentModal = ({
  isOpen,
  onClose,
  onSave,
  vets = [],
  workingHours = [],
  appointmentTypes = [],
  isAppointments = true,
  editAppointment = null,
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t } = useTranslation();

  const [petOwnerSearch, setPetOwnerSearch] = useState("");
  const [selectedPetOwner, setSelectedPetOwner] = useState(null);
  const [petOwnerPets, setPetOwnerPets] = useState([]);
  const [isLoadingPets, setIsLoadingPets] = useState(false);
  const [petOwnerSearchResults, setPetOwnerSearchResults] = useState([]);
  const [showPetOwnerDropdown, setShowPetOwnerDropdown] = useState(false);
  const [isSearchingPetOwners, setIsSearchingPetOwners] = useState(false);
  const [vetSearch, setVetSearch] = useState("");
  const [selectedVet, setSelectedVet] = useState(null);
  const [showVetDropdown, setShowVetDropdown] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [hasUserTouchedTime, setHasUserTouchedTime] = useState(false);
  const [editAppointmentData, setEditAppointmentData] = useState({
    id: null,
    pet_owner: "",
    pet: "",
    vet: "",
    reason: "",
    date: "",
    time: "",
    type: "",
  });

  // Refs for Flatpickr
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const timePickerRef = useRef(null);

  // Generate time slots based on working hours
  const generateTimeSlots = (workingHours) => {
    if (!workingHours || workingHours.length === 0) return [];

    const slots = [];
    workingHours.forEach(wh => {
      const startTime = wh.opening_time;
      const endTime = wh.closing_time;
      const dayKey = String(wh.day || '').toLowerCase();

      // Convert time strings to minutes for easier calculation
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);

      // Generate 30-minute slots
      for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
        const timeString = minutesToTime(minutes);
        slots.push({
          time: timeString,
          display: timeString,
          day: dayKey
        });
      }
    });

    return slots.sort((a, b) => a.time.localeCompare(b.time));
  };

  // Helper functions for time conversion
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Helper function to parse appointment_at datetime string and split into date and time
  const parseAppointmentAt = (appointmentAt) => {
    if (!appointmentAt) return { date: '', time: '' };

    try {
      // Normalize to UTC for reliable cross-browser parsing
      const normalized = appointmentAt.replace(' ', 'T') + (appointmentAt.includes('Z') ? '' : 'Z');
      const dateTime = new Date(normalized);

      // Extract local date in YYYY-MM-DD format
      const year = dateTime.getFullYear();
      const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
      const day = dateTime.getDate().toString().padStart(2, '0');
      const date = `${year}-${month}-${day}`;

      // Extract local time in HH:MM format
      const hours = dateTime.getHours().toString().padStart(2, '0');
      const minutes = dateTime.getMinutes().toString().padStart(2, '0');
      const time = `${hours}:${minutes}`;

      return { date, time };
    } catch (error) {
      console.error('❌ Error parsing appointment_at:', error);
      return { date: '', time: '' };
    }
  };

  // Helper function to combine date and time into appointment_at format (UTC)
  const combineDateTime = (date, time) => {
    if (!date || !time) return null;

    try {
      // Create a local Date object from the inputs
      const localDate = new Date(`${date}T${time}:00`);
      // Return as UTC ISO string to tell the backend exactly which point in time we mean
      return localDate.toISOString();
    } catch (error) {
      console.error('Error combining date and time:', error);
      return null;
    }
  };

  // When Pet Guardian changes, fetch their pets
  useEffect(() => {
    if (selectedPetOwner) {
      // Use pets from the search response if available, otherwise fall back to API call
      const petsFromSearch = selectedPetOwner.pets?.list || selectedPetOwner.pets || [];

      if (petsFromSearch.length > 0) {
        // Use pets from search response (no API call needed)
        setPetOwnerPets(petsFromSearch);

        // Auto-select if there's only one pet (only if not in edit mode or no pet already selected)
        if (petsFromSearch.length === 1 && (!editAppointment || !selectedPet)) {
          setSelectedPet(petsFromSearch[0]);
        } else if (!editAppointment) {
          setSelectedPet(null);
        }
        setIsLoadingPets(false);
      } else {
        // Fallback: fetch pets via API (for cases where search didn't include pets)
        setIsLoadingPets(true);
        petAPI.getOtherPets(selectedPetOwner.id)
          .then((res) => {
            const pets = res.data?.data || [];
            setPetOwnerPets(pets);

            // Auto-select if there's only one pet (only if not in edit mode or no pet already selected)
            if (pets.length === 1 && (!editAppointment || !selectedPet)) {
              setSelectedPet(pets[0]);
            } else if (!editAppointment) {
              setSelectedPet(null);
            }
          })
          .catch((error) => {
            console.error('Error fetching pets:', error);
            setPetOwnerPets([]);
            if (!editAppointment) {
              setSelectedPet(null);
            }
          })
          .finally(() => {
            setIsLoadingPets(false);
          });
      }
    } else {
      setPetOwnerPets([]);
      if (!editAppointment) {
        setSelectedPet(null);
      }
      setIsLoadingPets(false);
    }
  }, [selectedPetOwner, editAppointment, selectedPet]);

  // Pet Guardian search with debouncing
  useEffect(() => {
    if (!petOwnerSearch.trim()) {
      setPetOwnerSearchResults([]);
      setIsSearchingPetOwners(false);
      return;
    }
    setIsSearchingPetOwners(true);
    const timeoutId = setTimeout(() => {
      clinicAPI.searchGuardians(petOwnerSearch)
        .then((response) => {
          setPetOwnerSearchResults(response.data?.data || []);
          setIsSearchingPetOwners(false);
        })
        .catch((error) => {
          console.error('Error searching Pet Guardians:', error);
          setPetOwnerSearchResults([]);
          setIsSearchingPetOwners(false);
        });
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [petOwnerSearch]);

  // Generate time slots when working hours change
  useEffect(() => {
    const slots = generateTimeSlots(workingHours);
    setAvailableTimeSlots(slots);
  }, [workingHours]);

  // Filter time slots based on selected date and current time
  // - For future dates: show all available time slots
  // - For today: show only time slots that are 15+ minutes in the future
  const getAvailableTimeSlotsForDate = (selectedDate) => {
    if (!selectedDate) {
      return availableTimeSlots;
    }

    const selectedDay = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const selectedDateObj = new Date(selectedDate);
    const today = new Date();

    // Check if selected date is today
    const isToday = selectedDateObj.toDateString() === today.toDateString();

    let filteredSlots = availableTimeSlots.filter(slot => slot.day === selectedDay);

    if (isToday) {
      // Filter out past time slots for today
      const currentHour = today.getHours();
      const currentMinute = today.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      const originalSlotsCount = filteredSlots.length;
      filteredSlots = filteredSlots.filter(slot => {
        const [slotHour, slotMinute] = slot.time.split(':').map(Number);
        const slotTimeInMinutes = slotHour * 60 + slotMinute;

        // Show time slots that are at least 15 minutes in the future (reduced from 30 to be less restrictive)
        const isFutureSlot = slotTimeInMinutes >= (currentTimeInMinutes + 15);


        return isFutureSlot;
      });

    }
    return filteredSlots;
  };

  // Initialize Flatpickr for date picker only
  useEffect(() => {
    if (isOpen && dateInputRef.current) {
      // Initialize date picker
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }

      // Create array of available days based on working hours
      const dayMap = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      const availableDays = workingHours
        .map(wh => {
          const dayKey = String(wh.day || '').toLowerCase();
          return dayMap[dayKey];
        })
        .filter((d) => d !== undefined);
      // Enable working hours restriction and disable past dates
      const disableFunction = workingHours.length > 0 ? [
        function (date) {
          // Disable past dates
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date < today) {
            return true;
          }

          // Disable dates that are not in working hours
          const dayOfWeek = date.getDay();
          const isDisabled = !availableDays.includes(dayOfWeek);
          return isDisabled;
        }
      ] : [
        function (date) {
          // Disable past dates when no working hours are set
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isPastDate = date < today;
          return isPastDate;
        }
      ];

      // Initialize Flatpickr with proper configuration
      datePickerRef.current = flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        disable: disableFunction,
        onChange: function (selectedDates, dateStr) {
          setEditAppointmentData(prev => ({ ...prev, date: dateStr, time: "" }));
          setHasUserTouchedTime(false);
        }
      });

      // Set the date after Flatpickr is initialized - use a timeout to ensure it's set after initialization
      setTimeout(() => {
        if (editAppointmentData.date && datePickerRef.current) {
          datePickerRef.current.setDate(editAppointmentData.date);
        }
      }, 100);
    }

    return () => {
      if (datePickerRef.current) {
        datePickerRef.current.destroy();
      }
    };
  }, [isOpen, workingHours, editAppointmentData.date]);

  // Reset form when modal opens or when editAppointment changes
  useEffect(() => {
    if (isOpen) {
      if (editAppointment) {
        // Reset user-modified time flag on open
        setHasUserTouchedTime(false);

        // Simple and clean: extract date and time from appointment_at or use separate fields
        let appointmentDate = editAppointment.date;
        let appointmentTime = editAppointment.time;

        // If we have appointment_at, parse it to get date and time
        if (editAppointment.appointment_at && (!appointmentDate || !appointmentTime)) {
          const parsed = parseAppointmentAt(editAppointment.appointment_at);
          appointmentDate = appointmentDate || parsed.date;
          appointmentTime = appointmentTime || parsed.time;
        }


        // Populate form data with parsed values


        setEditAppointmentData({
          id: editAppointment.id,
          pet_owner: editAppointment.pet_owner || "",
          pet: editAppointment.pet || "",
          vet: editAppointment.vet || "",
          reason: editAppointment.reason || "",
          date: appointmentDate,
          time: appointmentTime,
          type: editAppointment.appointment_type_id || "",
        });

        // Set selected Pet Guardian if available
        if (editAppointment.pet_owner) {
          // Handle both string and object pet_owner data
          const petOwnerName = typeof editAppointment.pet_owner === 'string'
            ? editAppointment.pet_owner
            : `${editAppointment.pet_owner.first_name || ''} ${editAppointment.pet_owner.last_name || ''}`.trim();

          setPetOwnerSearch(petOwnerName);
          // Create a Pet Guardian object with the data we have, including last_completed_appointment_relation
          setSelectedPetOwner({
            id: editAppointment.pet_owner_id || (typeof editAppointment.pet_owner === 'object' ? editAppointment.pet_owner.id : null),
            username: petOwnerName,
            first_name: typeof editAppointment.pet_owner === 'object' ? editAppointment.pet_owner.first_name : editAppointment.pet_owner.split(' ')[0] || '',
            last_name: typeof editAppointment.pet_owner === 'object' ? editAppointment.pet_owner.last_name : editAppointment.pet_owner.split(' ').slice(1).join(' ') || '',
            // Include the last_completed_appointment_relation from the nested object
            last_completed_appointment_relation: typeof editAppointment.pet_owner === 'object' ? editAppointment.pet_owner.last_completed_appointment_relation : null
          });
        } else {
          setPetOwnerSearch("");
          setSelectedPetOwner(null);
        }

        // Set selected vet if available
        if (editAppointment.vet) {
          // Handle both string and object vet data
          const vetName = typeof editAppointment.vet === 'string'
            ? editAppointment.vet
            : `${editAppointment.vet.first_name || ''} ${editAppointment.vet.last_name || ''}`.trim();

          setVetSearch(vetName);

          // Find the vet in the vets array by matching the ID or name
          const foundVet = vets.find(v => {
            // First try to match by ID if available
            if (editAppointment.vet_id && v.user_id === editAppointment.vet_id) {
              return true;
            }
            // Also try to match by vet object ID
            if (typeof editAppointment.vet === 'object' && editAppointment.vet.id && v.user_id === editAppointment.vet.id) {
              return true;
            }
            // Fallback to name matching
            const vetNameFromVet = v.user?.name || `${v.user?.first_name || ''} ${v.user?.last_name || ''}`.trim();
            return vetNameFromVet === vetName;
          });
          setSelectedVet(foundVet || null);
        } else {
          setVetSearch("");
          setSelectedVet(null);
        }

        // Set selected pet if available - this will be used as a fallback
        // The actual pet selection will happen in the useEffect that fetches pets
        if (editAppointment.pet) {
          // Handle both string and object pet data
          const petName = typeof editAppointment.pet === 'string'
            ? editAppointment.pet
            : editAppointment.pet.name;

          // Store the pet data for later auto-selection
          const petData = {
            id: editAppointment.pet_id || (typeof editAppointment.pet === 'object' ? editAppointment.pet.id : null),
            name: petName,
            species: typeof editAppointment.pet === 'object' ? editAppointment.pet.species : null,
            breed: typeof editAppointment.pet === 'object' ? editAppointment.pet.breed : null,
            gender: typeof editAppointment.pet === 'object' ? editAppointment.pet.gender : null
          };

          // Don't set selectedPet here - let the pet fetching logic handle it
        } else {
          setSelectedPet(null);
        }
      }

      setPetOwnerPets([]);
      setIsLoadingPets(false);
      setPetOwnerSearchResults([]);
      setShowPetOwnerDropdown(false);
      setIsSearchingPetOwners(false);
      setShowVetDropdown(false);
      setShowTimeDropdown(false);
    }
  }, [isOpen, editAppointment, vets]);

  // Fetch pets for the selected Pet Guardian in edit mode
  useEffect(() => {
    if (editAppointment && selectedPetOwner && selectedPetOwner.id) {
      // Use pets from the search response if available, otherwise fall back to API call
      const petsFromSearch = selectedPetOwner.pets?.list || selectedPetOwner.pets || [];

      if (petsFromSearch.length > 0) {
        // Use pets from search response (no API call needed)
        setPetOwnerPets(petsFromSearch);
        setIsLoadingPets(false);

        // Auto-select the pet from editAppointment if it exists in the pets list
        if (editAppointment.pet && !selectedPet) {
          const petToSelect = petsFromSearch.find(p => {
            // Match by ID if available
            if (editAppointment.pet_id && p.id === editAppointment.pet_id) {
              return true;
            }
            // Match by name as fallback
            const petName = typeof editAppointment.pet === 'string'
              ? editAppointment.pet
              : editAppointment.pet.name;
            return p.name === petName;
          });

          if (petToSelect) {
            setSelectedPet(petToSelect);
          }
        }
      } else {
        // Fallback: fetch pets via API (for cases where search didn't include pets)
        setIsLoadingPets(true);
        petAPI.getOtherPets(selectedPetOwner.id)
          .then((res) => {
            const pets = res.data?.data || [];
            setPetOwnerPets(pets);

            // Auto-select the pet from editAppointment if it exists in the fetched pets
            if (editAppointment.pet && !selectedPet) {
              const petToSelect = pets.find(p => {
                // Match by ID if available
                if (editAppointment.pet_id && p.id === editAppointment.pet_id) {
                  return true;
                }
                // Match by name as fallback
                const petName = typeof editAppointment.pet === 'string'
                  ? editAppointment.pet
                  : editAppointment.pet.name;
                return p.name === petName;
              });

              if (petToSelect) {
                setSelectedPet(petToSelect);
              }
            }
          })
          .catch((error) => {
            console.error('Error fetching pets for edit mode:', error);
            setPetOwnerPets([]);
          })
          .finally(() => {
            setIsLoadingPets(false);
          });
      }
    }
  }, [editAppointment, selectedPetOwner, selectedPet]);

  // Debug: Log when editAppointmentData changes
  useEffect(() => {
  }, [editAppointmentData]);

  // Update Flatpickr when the date changes
  useEffect(() => {
    if (datePickerRef.current && editAppointmentData.date) {
      datePickerRef.current.setDate(editAppointmentData.date);
    }
  }, [editAppointmentData.date]);

  // Initialize time from appointment_at only when needed (and not after user change)
  useEffect(() => {
    if (!isOpen) return;
    if (!editAppointment || !editAppointment.appointment_at) return;
    if (hasUserTouchedTime) return;
    if (editAppointmentData.time) return;
    const parsedTime = parseAppointmentAt(editAppointment.appointment_at).time;
    if (parsedTime) {
      setEditAppointmentData(prev => ({ ...prev, time: parsedTime }));
    }
  }, [isOpen, editAppointment, hasUserTouchedTime, editAppointmentData.time]);

  // Force re-render of time display when component mounts or appointment changes
  useEffect(() => {
    if (isOpen && editAppointment && editAppointmentData.time) {
      // Force a re-render by updating a state variable
      setShowTimeDropdown(false);
    }
  }, [isOpen, editAppointment, editAppointmentData.time]);

  // Additional useEffect to ensure time is set after a delay (guarded by user interaction)
  useEffect(() => {
    if (!isOpen || !editAppointment || !editAppointment.appointment_at) return;
    if (hasUserTouchedTime || editAppointmentData.time) return;
    const timer = setTimeout(() => {
      const parsedTime = parseAppointmentAt(editAppointment.appointment_at).time;
      if (!hasUserTouchedTime && !editAppointmentData.time && parsedTime) {
        setEditAppointmentData(prev => ({ ...prev, time: parsedTime }));
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [isOpen, editAppointment, hasUserTouchedTime, editAppointmentData.time]);

  const handleSave = async () => {
    // Combine date and time into appointment_at format
    const appointmentAt = combineDateTime(editAppointmentData.date, editAppointmentData.time);

    const appointment = {
      id: editAppointmentData.id || Date.now(),
      pet_owner_id: selectedPetOwner ? selectedPetOwner.id : null,
      pet_id: selectedPet ? selectedPet.id : null,
      vet_id: selectedVet ? selectedVet.user_id : null,
      reason: editAppointmentData.reason,
      date: editAppointmentData.date,
      time: editAppointmentData.time,
      appointment_at: appointmentAt, // Include the combined datetime
      appointment_type_id: editAppointmentData.type,
      // Include the full objects for proper display updates
      pet_owner: selectedPetOwner,
      pet: selectedPet,
      vet: selectedVet,
    };

    await onSave(appointment);
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="reception-modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)'
      }}>
        <div className="reception-modal" style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}>
          <div className="reception-modal-header" style={{
            padding: '0 32px 16px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827'
            }}>
              ✏️ {isAppointments
                ? t("receptionAppointments.edit.title", { defaultValue: "Edit Appointment" })
                : t("receptionAppointments.edit.titleOutpatient", { defaultValue: "Edit Outpatient Visit" })}
            </h2>
            <button
              className="reception-modal-close"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '8px',
                borderRadius: '8px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              ✕
            </button>
          </div>
          <form
            className="reception-modal-form"
            onSubmit={(e) => {
              e.preventDefault();
            }}
            style={{
              paddingLeft: '32px',
              paddingRight: '32px'
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  👤 {t("receptionAppointments.create.fields.petGuardian.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => setShowPetOwnerDropdown(!showPetOwnerDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <span style={{ color: selectedPetOwner ? '#111827' : '#9ca3af' }}>
                      {selectedPetOwner
                        ? selectedPetOwner.display_name || selectedPetOwner.full_name || `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}`
                        : t("receptionAppointments.create.fields.petGuardian.placeholder")
                      }
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showPetOwnerDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Dropdown Content */}
                  {showPetOwnerDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '4px'
                    }}>
                      {/* Search Input Inside Dropdown */}
                      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionAppointments.create.fields.petGuardian.placeholder")}
                          value={petOwnerSearch}
                          onChange={(e) => {
                            setPetOwnerSearch(e.target.value);
                            // Clear selected Pet Guardian when user starts typing to search for a new one
                            if (selectedPetOwner && e.target.value) {
                              setSelectedPetOwner(null);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Results List */}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {isSearchingPetOwners ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280'
                          }}>
                            <div style={{
                              display: 'inline-block',
                              width: '20px',
                              height: '20px',
                              border: '2px solid #e5e7eb',
                              borderTop: '2px solid #3b82f6',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            <div style={{ marginTop: '8px', fontSize: '14px' }}>{t("common.loading")}</div>
                          </div>
                        ) : petOwnerSearchResults.length > 0 ? (
                          petOwnerSearchResults.map((g) => (
                            <div
                              key={g.id}
                              style={{
                                cursor: "pointer",
                                padding: "12px 16px",
                                borderBottom: '1px solid #f3f4f6',
                                transition: 'background-color 0.2s'
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedPetOwner(g);
                                setShowPetOwnerDropdown(false);
                                setPetOwnerSearchResults([]);
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: '500', color: '#111827' }}>
                                  {g.display_name || g.full_name || `${g.first_name} ${g.last_name}`}
                                </div>
                                {g.last_completed_appointment_relation && (
                                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                    Last visit: {new Date(g.last_completed_appointment_relation.appointment_at).toLocaleDateString()} ({g.last_completed_appointment_relation.status})
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : petOwnerSearch.trim() ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            No Pet Guardians found
                          </div>
                        ) : (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            Start typing to search Pet Guardians...
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  🐾 {t("receptionAppointments.create.fields.pet.label")}
                </span>
                <select
                  className="reception-modal-input"
                  value={selectedPet ? selectedPet.name : ""}
                  onChange={(e) => {
                    const selectedPetName = e.target.value;
                    const pet = petOwnerPets.find(p => p.name === selectedPetName);
                    setSelectedPet(pet || null);
                  }}
                  disabled={!selectedPetOwner || isLoadingPets || petOwnerPets.length === 0}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: (!selectedPetOwner || isLoadingPets) ? '#f9fafb' : 'white',
                    cursor: (!selectedPetOwner || isLoadingPets) ? 'not-allowed' : 'pointer'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">
                    {!selectedPetOwner
                      ? t("receptionAppointments.create.fields.pet.placeholder.selectGuardian")
                      : isLoadingPets
                        ? t("common.loading")
                        : petOwnerPets.length === 0
                          ? t("receptionAppointments.create.fields.pet.placeholder.none")
                          : t("receptionAppointments.create.fields.pet.placeholder.select")
                    }
                  </option>
                  {!isLoadingPets && petOwnerPets.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {isLoadingPets && (
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    pointerEvents: 'none'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #e5e7eb',
                      borderTop: '2px solid #3b82f6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontWeight: '500'
                    }}>
                      {t("common.loading")}
                    </span>
                  </div>
                )}
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  👨‍⚕️ {t("receptionAppointments.create.fields.vet.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => setShowVetDropdown(!showVetDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'white'
                    }}
                  >
                    <span style={{ color: selectedVet ? '#111827' : '#9ca3af' }}>
                      {selectedVet ? (selectedVet.user?.name || selectedVet.name || t("receptionAppointments.create.fields.vet.placeholder")) : t("receptionAppointments.create.fields.vet.placeholder")}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showVetDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Dropdown Content */}
                  {showVetDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '300px',
                      overflow: 'hidden',
                      marginTop: '4px'
                    }}>
                      {/* Search Input Inside Dropdown */}
                      <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder={t("receptionAppointments.create.fields.vet.searchPlaceholder")}
                          value={vetSearch}
                          onChange={(e) => setVetSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Results List */}
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {vets.filter((v) => {
                          const displayName = v.user?.name || `Vet ${v.user_id}`;
                          return displayName && displayName.toLowerCase().includes(vetSearch.toLowerCase());
                        }).length > 0 ? (
                          vets
                            .filter((v) => {
                              const displayName = v.user?.name || `Vet ${v.user_id}`;
                              return displayName && displayName.toLowerCase().includes(vetSearch.toLowerCase());
                            })
                            .map((v) => {
                              const displayName = v.user?.name || `Vet ${v.user_id}`;
                              return (
                                <div
                                  key={v.user_id}
                                  style={{
                                    cursor: "pointer",
                                    padding: "12px 16px",
                                    borderBottom: '1px solid #f3f4f6',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedVet(v);
                                    setVetSearch(displayName);
                                    setShowVetDropdown(false);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {displayName}
                                </div>
                              );
                            })
                        ) : vetSearch.trim() ? (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.vet.noResults")}
                          </div>
                        ) : (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            {t("receptionAppointments.create.fields.vet.searchPrompt")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  📝 {t("receptionAppointments.create.fields.reason.label")}
                </span>
                <input
                  type="text"
                  className="reception-modal-input"
                  value={editAppointmentData.reason}
                  onChange={(e) =>
                    setEditAppointmentData((a) => ({
                      ...a,
                      reason: e.target.value,
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
            </div>


            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '' }}>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  📅 {isAppointments ? "Appointment Date" : "Visit Date"}
                </span>
                <input
                  ref={dateInputRef}
                  type="text"
                  className="reception-modal-input"
                  placeholder={t("receptionAppointments.create.fields.date.placeholder")}
                  value={editAppointmentData.date || ""}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    cursor: 'pointer',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  ⏰ {isAppointments
                    ? t("receptionAppointments.create.fields.time.label")
                    : t("receptionAppointments.create.fields.timeOutpatient.label")}
                </span>
                <div style={{ position: 'relative' }}>
                  {/* Time Dropdown Button */}
                  <div
                    className="reception-modal-input"
                    onClick={() => {
                      const availableSlots = getAvailableTimeSlotsForDate(editAppointmentData.date);
                      if (availableSlots.length > 0) {
                        setShowTimeDropdown(!showTimeDropdown);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      outline: 'none',
                      cursor: getAvailableTimeSlotsForDate(editAppointmentData.date).length > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: getAvailableTimeSlotsForDate(editAppointmentData.date).length === 0 ? '#f9fafb' : 'white'
                    }}
                  >
                    <span style={{ color: editAppointmentData.time ? '#111827' : '#9ca3af' }}>
                      {editAppointmentData.time || t("receptionAppointments.create.fields.time.placeholder")}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {showTimeDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* Time Dropdown Content */}
                  {showTimeDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 10,
                      maxHeight: '200px',
                      overflow: 'auto',
                      marginTop: '4px'
                    }}>
                      {getAvailableTimeSlotsForDate(editAppointmentData.date).length > 0 ? (
                        getAvailableTimeSlotsForDate(editAppointmentData.date).map((slot, index) => (
                          <div
                            key={index}
                            style={{
                              cursor: "pointer",
                              padding: "12px 16px",
                              borderBottom: '1px solid #f3f4f6',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              backgroundColor: slot.time === editAppointmentData.time ? '#e0f2fe' : 'transparent',
                              borderLeft: slot.time === editAppointmentData.time ? '3px solid #3b82f6' : '3px solid transparent',
                              color: slot.time === editAppointmentData.time ? '#1d4ed8' : '#111827',
                              fontWeight: slot.time === editAppointmentData.time ? '600' : '400'
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditAppointmentData(prev => ({ ...prev, time: slot.time }));
                              setHasUserTouchedTime(true);
                              setShowTimeDropdown(false);
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            <span style={{ fontSize: '16px', fontWeight: '500' }}>
                              {slot.display}
                            </span>
                            <span style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              backgroundColor: '#f3f4f6',
                              padding: '2px 8px',
                              borderRadius: '4px'
                            }}>
                              {slot.day}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div style={{
                          padding: '20px',
                          textAlign: 'center',
                          color: '#6b7280',
                          fontSize: '14px'
                        }}>
                          {editAppointmentData.date ?
                            (() => {
                              const selectedDateObj = new Date(editAppointmentData.date);
                              const today = new Date();
                              const isSelectedDateToday = selectedDateObj.toDateString() === today.toDateString();
                              return isSelectedDateToday ? t("receptionAppointments.create.fields.time.noSlotsToday") : t("receptionAppointments.create.fields.time.noSlotsDay");
                            })() :
                            t("receptionAppointments.create.fields.time.selectDateFirst")
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  🕒 {t("receptionAppointments.create.fields.lastVisit.label")}
                </span>
                <input
                  type="text"
                  className="reception-modal-input"
                  value={selectedPetOwner?.last_completed_appointment_relation
                    ? t("receptionAppointments.create.fields.lastVisit.dropdown", { date: new Date(selectedPetOwner.last_completed_appointment_relation.appointment_at).toLocaleDateString(), status: selectedPetOwner.last_completed_appointment_relation.status })
                    : t("receptionAppointments.create.fields.lastVisit.noVisits")
                  }
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: '#f9fafb',
                    color: '#6b7280'
                  }}
                />
              </label>
              <label className="reception-modal-label" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  {t("receptionAppointments.create.fields.type.label")}
                </span>
                <select
                  className="reception-modal-input"
                  value={editAppointmentData.type || ""}
                  onChange={(e) =>
                    setEditAppointmentData((a) => ({
                      ...a,
                      type: e.target.value,
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: 'white',
                    color: editAppointmentData.type ? '#111827' : '#9ca3af'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="" disabled>
                    {t("receptionAppointments.create.fields.type.placeholder")}
                  </option>
                  {appointmentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.title}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="reception-modal-actions" style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end',
              paddingTop: '10px',
              borderTop: '1px solid #e5e7eb'
              , marginTop: '15px'
            }}>
              <button
                type="button"
                className="reception-action-btn"
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = 'white';
                }}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="reception-action-btn"
                onClick={handleSave}
                style={{
                  width: '220px',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563eb';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3b82f6';
                  e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
              >
                💾 {isAppointments ? t("receptionAppointments.create.buttons.save") : t("receptionAppointments.create.buttons.saveVisit", { defaultValue: "Save Visit" })}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditAppointmentModal;
